/**
 * "Last used" sign-in hint.
 *
 * On a return visit the login/signup page surfaces a small "Last used" badge on
 * whichever method the visitor last authenticated with, so they don't hesitate
 * between Google / LinkedIn / email. Because same-verified-email logins all
 * auto-link to one account (see `src/lib/auth/index.ts` account hooks), this is
 * a pure UX nicety — picking a different method is harmless either way.
 *
 * Stored in a first-party, non-httpOnly cookie (SSR reads it via `cookies()`)
 * carrying only the method name — no PII. Set client-side at the moment the user
 * triggers a method; best-effort, never blocks auth.
 */
import { setClientCookie } from "@/lib/cookies/client";

export const LAST_METHOD_COOKIE = "ld_last_method";

export type AuthMethod = "google" | "linkedin" | "email";

// 180 days — long enough that an occasional returning user still sees the hint.
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

export function isAuthMethod(value: unknown): value is AuthMethod {
	return value === "google" || value === "linkedin" || value === "email";
}

/**
 * Client-only. Remember the method the user just used. No-op during SSR.
 */
export function rememberAuthMethod(method: AuthMethod): void {
	if (typeof document === "undefined") return;
	setClientCookie(LAST_METHOD_COOKIE, method, {
		maxAgeSeconds: MAX_AGE_SECONDS,
	});
}
